import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Crown, Receipt, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { ROUTES } from "@/app/routes";
import {
  type AccountAdmin,
  grantAccountAdmin,
  listAccountAdmins,
  listAccountUsers,
  revokeAccountAdmin,
  updateAccountName,
} from "@/features/identity/identity.api";
import {
  type UpdateAccountInput,
  updateAccountInputSchema,
} from "@/features/identity/schemas/inputs.schema";
import { SettingsLayout } from "@/features/settings/settings-layout";
import { useAuth } from "@/shared/auth/auth.context";
import { TextField } from "@/shared/components/form/fields/text-field";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Form } from "@/shared/components/ui/form";
import { UserAvatar } from "@/shared/components/user-avatar";
import { UserPicker } from "@/shared/components/user-picker/user-picker";
import { UsagePanel } from "@/shared/entitlements/components/usage-panel";

export function AccountSettingsRoute() {
  const auth = useAuth();
  const qc = useQueryClient();
  const accountId = auth.account?.id ?? "";
  const isOwner = auth.user?.accountRole === "owner";
  const [grantUserId, setGrantUserId] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<UpdateAccountInput>({
    resolver: zodResolver(updateAccountInputSchema),
    defaultValues: { displayName: auth.account?.name ?? "" },
  });

  const admins = useQuery({
    queryKey: ["account-admins", accountId],
    queryFn: () =>
      accountId ? listAccountAdmins(accountId) : Promise.resolve({ items: [] as AccountAdmin[] }),
    enabled: !!accountId,
  });

  // Owners and admins are already listed above, so only plain members can be granted.
  const users = useQuery({
    queryKey: ["account-users", accountId],
    queryFn: () => listAccountUsers(accountId),
    enabled: !!accountId && isOwner,
  });
  const grantableMembers = users.data?.items.filter((u) => u.accountRole === "member") ?? [];

  const saveName = useMutation({
    mutationFn: (newName: string) => updateAccountName(accountId, newName),
    onSuccess: async () => {
      setSavedNote("Saved.");
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
  // Both lists change on a grant or a revoke: the person moves between the
  // admins list and the picker's candidates.
  const refreshPeople = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["account-admins", accountId] }),
      qc.invalidateQueries({ queryKey: ["account-users", accountId] }),
    ]);
  const grant = useMutation({
    mutationFn: (userId: string) => grantAccountAdmin(accountId, userId),
    onSuccess: async () => {
      setGrantUserId(null);
      await refreshPeople();
    },
  });
  const revoke = useMutation({
    mutationFn: (userId: string) => revokeAccountAdmin(accountId, userId),
    onSuccess: refreshPeople,
  });

  async function onSubmitName(values: UpdateAccountInput) {
    setSavedNote(null);
    setServerError(null);
    try {
      await saveName.mutateAsync(values.displayName);
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Could not save.");
    }
  }

  useEffect(() => {
    if (!savedNote) return;
    const t = setTimeout(() => setSavedNote(null), 2500);
    return () => clearTimeout(t);
  }, [savedNote]);

  if (!accountId) {
    return <div className="px-6 py-8 text-sm text-text-tertiary">No account context.</div>;
  }

  return (
    // No page container: renders as a section inside the /settings shell,
    // which owns the max-width, padding, and the "Settings" heading.
    <SettingsLayout>
      <div className="flex w-full flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold tracking-tight">General</h2>
            <p className="mt-1 text-sm text-text-secondary">
              Account-level controls, visible to Account Owner and Admin.
            </p>
          </div>
          {/* Fix round 1, finding 1: the account billing page (task 5) has no
            other entry point an owner would stumble into on their own (the
            redeem screen only sends people here after redeeming a code).
            Account settings is the sibling account-level page every owner
            already opens for plan + usage (`UsagePanel` right below), so a
            "Billing" link here, gated the same `isOwner` way the admin-grant
            form below already is, matches this page's own established
            pattern rather than inventing a new one. */}
          {isOwner ? (
            <Button asChild variant="outline">
              <Link to={ROUTES.accountBilling.path}>
                <Receipt className="size-3.5" aria-hidden="true" />
                Billing
              </Link>
            </Button>
          ) : null}
        </div>

        <UsagePanel />

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Name</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmitName)}
                className="flex flex-col gap-3"
                noValidate
              >
                <TextField control={form.control} name="displayName" label="Account name" />
                {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}
                {savedNote ? <p className="text-sm text-success-fg">{savedNote}</p> : null}
                <div>
                  <Button type="submit" disabled={form.formState.isSubmitting}>
                    {form.formState.isSubmitting ? "Saving…" : "Save"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold">Owner & admins</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {admins.isLoading ? (
              <p className="text-sm text-text-tertiary">Loading…</p>
            ) : (
              <ul className="flex flex-col divide-y divide-border">
                {admins.data?.items.map((a) => (
                  <li key={a.userId} className="flex items-center gap-3 py-2.5">
                    <UserAvatar
                      displayName={a.displayName}
                      avatarUrl={a.avatarUrl}
                      className="size-7"
                      fallbackClassName="bg-pulse text-3xs font-semibold text-white"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {a.displayName}
                        {a.accountRole === "owner" ? (
                          <Crown className="size-3 text-amber-500" />
                        ) : null}
                      </div>
                      <div className="truncate font-mono text-2xs tracking-wider text-text-tertiary">
                        {a.email}
                      </div>
                    </div>
                    {isOwner && a.accountRole === "admin" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revoke.mutate(a.userId)}
                        aria-label="Revoke admin"
                      >
                        <X className="size-3.5" />
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {isOwner ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (grantUserId) grant.mutate(grantUserId);
                }}
                className="flex flex-col gap-2 border-t border-border pt-4"
              >
                <p className="text-sm font-medium">Make someone an admin</p>
                {users.isLoading ? (
                  <p className="text-sm text-text-tertiary">Loading people…</p>
                ) : users.isError ? (
                  <p className="text-sm text-destructive">
                    Couldn't load the people in this account.
                  </p>
                ) : grantableMembers.length === 0 ? (
                  <p className="text-sm text-text-tertiary">
                    Everyone in this account is already an owner or admin.
                  </p>
                ) : (
                  <div className="flex gap-2">
                    <UserPicker
                      value={grantUserId}
                      onChange={setGrantUserId}
                      options={grantableMembers}
                      emptyLabel="Choose a person"
                      ariaLabel="Person to make admin"
                      className="min-w-0 flex-1"
                    />
                    <Button type="submit" disabled={grant.isPending || !grantUserId}>
                      {grant.isPending ? "Granting…" : "Grant"}
                    </Button>
                  </div>
                )}
                {grant.isError ? (
                  <p className="text-sm text-destructive">
                    {grant.error instanceof Error ? grant.error.message : "Could not grant admin."}
                  </p>
                ) : null}
              </form>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
